import React from 'react';
import PropTypes from 'prop-types';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { Card } from 'components/legacy/Card';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import { Link } from 'components/core/Link';
import { H1, H2, Paragraph } from 'components/core/Typography';
import {
	Page,
	Section,
	Container,
	Group,
	Row,
	Column,
} from 'components/core/Page';

import { FacilityAddress } from 'components/facility/FacilityAddress';
import { FacilityBreadcrumbs } from 'components/facility/FacilityBreadcrumbs';
import { NearbyFacilities } from 'components/facility/NearbyFacilities';
import { FacilitySchema } from 'components/facility/FacilitySchema';
import { SearchBox } from 'components/search/SearchBox';

import shieldImage from 'assets/images/icons/shield.svg';
import dollarImage from 'assets/images/icons/circle-dollar.svg';

import './OGFFacilityPage.scss';
import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { compose } from 'recompose';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import MapPin from 'components/svg/MapPin';

export const enhance = compose(
	withShowHide('facilityPhone'),
	withPageViewTracking(PAGE_SUB_TYPES.OON_FACILITY),
);

export function OGFFacilityPage({
	actions,
	facility,
	handleSearch,
	nearbyFacilities,
	facilityPhoneShowing,
	onShowFacilityPhone,
	trackingPixel,
}) {
	return (
		<Page
			className="ogf-facility-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_SUB_TYPES.OON_FACILITY}
		>
			<FacilitySchema
				facility={facility}
				phone={facility.facilityPhone}
			/>
			<Section>
				<Container className="facility-header">
					<FacilityBreadcrumbs facility={facility} />
					<H1
						size="large"
						className="facility-name"
					>
						{facility.name} - {facility.location.city} -
						{facility.location.streetAddress1}
					</H1>
					<Group>
						{facility.facilityPhone && (
							<div>
								{!facilityPhoneShowing && (
									<Button
										onClick={onShowFacilityPhone}
										className="request-facility-phone"
									>
										Have billing or other questions for this
										facility?
									</Button>
								)}
								{facilityPhoneShowing && (
									<span className="facility-phone">
										{facility.facilityPhone}
									</span>
								)}
							</div>
						)}
					</Group>
					<Group>
						<FacilityAddress address={facility.location} />
					</Group>
					<Paragraph className="facility-disclaimer">
						We're sorry, we are unable to provide free booking and
						reservation services for this facility. See one of our
						preferred facilities below.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<Row>
						<Column xlarge={8}>
							<Section className="why-sparefoot">
								<H2 className="why-sparefoot-headline">
									Find storage near you
								</H2>
								<ul>
									<li className="why-reason">
										<Image
											lazyLoad
											className="why-image"
											src={dollarImage}
											role="presentation"
										/>
										<Paragraph
											className="why-text"
											last
										>
											Reserve now, pay later
										</Paragraph>
									</li>
									<li className="why-reason">
										<Image
											lazyLoad
											className="why-image"
											src={shieldImage}
											role="presentation"
										/>
										<Paragraph
											className="why-text"
											last
										>
											Cancel for free anytime
										</Paragraph>
									</li>
								</ul>
							</Section>
							<Section>
								<SearchBox
									onSearch={handleSearch}
									icon={<MapPin fill="gray" />}
									useBrowserLocation
								/>
							</Section>
							{!!nearbyFacilities && (
								<NearbyFacilities
									location={facility.location}
									facilities={nearbyFacilities}
									withUnits
									getFacilityPhoneNumber={
										actions.getFacilityPhoneNumber
									}
									handleSearch={handleSearch}
									searchId={facility.nearbyFacilitiesSearchId}
								/>
							)}
						</Column>
						<Column xlarge={4}>
							<Button
								secondary
								fullWidth
								id="claim-listing"
								className="claim-listing"
								rel="nofollow"
								href="/claim-listing"
							>
								Is this your facility? Claim your listing!
							</Button>
							{!!facility.staticMapUrl && (
								<Section className="nearby-facilities-map">
									<Card header="Map of Nearby Facilities">
										<Image
											lazyLoad
											src={facility.staticMapUrl}
										/>
									</Card>
								</Section>
							)}
							<Section>
								<Card header="What is SelfStorage.com?">
									<div className="card-content ogf-benefits">
										<Paragraph>
											SelfStorage.com is the one-stop shop
											for self-storage, helping you find
											the best prices on storage units
											near you.
										</Paragraph>
										<Paragraph>
											We partner with storage facilities
											across the U.S. to list more than
											40,000 locations—the most of any
											storage website.
										</Paragraph>
										<Paragraph>
											By using our advanced filtering and
											search tools, you can view and
											compare storage options close to you
											without the hassle of calling around
											for prices.
										</Paragraph>
										<Paragraph>
											Easily sort through self-storage
											amenities like climate control and
											24-hour access as well as different
											storage options for your car, boat
											or RV.
										</Paragraph>
										<Paragraph>
											Reserve your unit for FREE in a
											matter of minutes, whether online or
											by phone.
										</Paragraph>
										<Paragraph>
											Looking for self-storage units in{' '}
											{facility.location.city}?{' '}
											<Link
												secondary
												href={facility.cityUrl}
											>
												SelfStorage.com has{' '}
												{facility.totalNearbyFacilities}{' '}
												storage facilities within 15
												miles
											</Link>
											.
										</Paragraph>
									</div>
								</Card>
							</Section>
						</Column>
					</Row>
				</Container>
			</Section>
		</Page>
	);
}

OGFFacilityPage.propTypes = {
	actions: PropTypes.shape({
		getFacilityPhoneNumber: PropTypes.func.isRequired,
	}).isRequired,
	facility: PropTypes.object.isRequired,
	handleSearch: PropTypes.func.isRequired,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	facilityPhoneShowing: PropTypes.bool.isRequired,
	onShowFacilityPhone: PropTypes.func.isRequired,
	trackingPixel: PropTypes.string.isRequired,
};

export default enhance(OGFFacilityPage);
